'use client'

import { useState } from 'react'
import Link from 'next/link'
import { ChevronDownIcon, ChevronUpIcon, XIcon } from 'lucide-react'
import { Lektionen } from '@/payload-types'
import { formatSlug } from '@/hooks/formatSlug'

interface MobileNavProps {
  lessons: Lektionen[]
  slug: string
  currentLection: string
  currentSection: string
  academyTitle?: string | null
  isAnimating: boolean
  buttonPosition: { top: number; left: number; width: number; height: number }
  onClose: () => void
}

export function MobileNav({
  lessons,
  slug,
  currentLection,
  currentSection,
  academyTitle,
  isAnimating,
  buttonPosition,
  onClose,
}: MobileNavProps) {
  const [expandedLesson, setExpandedLesson] = useState<string | null>(currentLection)

  const toggleLesson = (lessonSlug: string) => {
    setExpandedLesson(lessonSlug)
  }

  const createSectionSlug = (navText: string | null | undefined): string => {
    if (!navText) return ''
    return formatSlug(navText)
  }

  const topOffset = 140
  const initialTop = topOffset
  const initialHeight = buttonPosition.height
  const containerHeight = isAnimating ? `calc(100vh - ${topOffset}px)` : `${initialHeight}px`

  return (
    <div className="pointer-events-none fixed inset-0 z-50 w-full lg:hidden">
      <div
        className="pointer-events-auto absolute flex flex-col overflow-hidden bg-custom-blue shadow-xl transition-[height,top] duration-300 ease-out"
        style={{
          top: isAnimating ? `${topOffset}px` : `${initialTop}px`,
          left: 0,
          width: '100%',
          height: containerHeight,
          borderRadius: isAnimating ? '0' : '0.5rem',
        }}
      >
        <div
          className={`bg-nav-background transition-opacity duration-300 flex-1 overflow-y-auto ${isAnimating ? 'opacity-100 delay-150' : 'opacity-0'}`}
          style={{
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
          }}
        >
          <nav className="p-4">
            {lessons.map((lesson, index) => {
              const lessonSlug = lesson.slug || ''
              const isExpanded = expandedLesson === lessonSlug
              const isActive = lessonSlug === currentLection
              const sectionCount = lesson.academyLections?.length || 0

              return (
                <div key={lesson.id} className="mb-2">
                  <button
                    onClick={() => toggleLesson(lessonSlug)}
                    className={`flex w-full items-center justify-between rounded-lg px-4 py-3 text-left transition-colors ${
                      isActive
                        ? 'bg-nav-item-active text-nav-text-active'
                        : 'bg-nav-item-bg text-nav-text hover:bg-nav-item-hover'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <span className="text-xl font-medium">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <span className="text-xl font-medium uppercase">- {lesson.headline}</span>
                    </div>
                    {sectionCount > 0 && (
                      <div className="flex items-center space-x-2">
                        <span
                          className={`transition-transform duration-200 ${
                            isExpanded ? 'rotate-180' : 'rotate-0'
                          }`}
                        >
                          <ChevronDownIcon className="h-5 w-5" />
                        </span>
                      </div>
                    )}
                  </button>

                  {isExpanded && sectionCount > 0 && (
                    <div className="mt-1 space-y-1 pl-3">
                      {lesson.academyLections?.map((section) => {
                        if (
                          typeof section !== 'object' ||
                          section === null ||
                          !('navText' in section)
                        )
                          return null

                        const sectionData = section as { navText: string; id?: string }
                        const sectionSlug = createSectionSlug(sectionData.navText)
                        const isSectionActive =
                          lessonSlug === currentLection && sectionSlug === currentSection

                        return (
                          <Link
                            key={sectionData.id || sectionData.navText}
                            href={`/academy/${slug}/${lessonSlug}/${sectionSlug}`}
                            onClick={onClose}
                            className={`block rounded-lg px-4 py-2 text-lg transition-colors ${
                              isSectionActive
                                ? 'bg-nav-item-active/20 text-nav-item-active font-medium'
                                : 'text-nav-text hover:bg-nav-item-hover'
                            }`}
                          >
                            {sectionData.navText}
                          </Link>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </nav>
        </div>
      </div>
    </div>
  )
}
